import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { formatPrice } from "../../lib/utils";

const METHODS = [
  { id: "bank_transfer", label: "Bank transfer" },
  { id: "mtn", label: "MTN Mobile Money" },
  { id: "airtel", label: "Airtel Money" },
  { id: "cash", label: "Cash" },
  { id: "other", label: "Other" },
];
const METHOD_LABEL = Object.fromEntries(METHODS.map((m) => [m.id, m.label]));
const STATUS_VARIANT = { pending: "primary", paid: "accent", cancelled: "default" };

/**
 * Gift cards valid at any business are paid to SHUWA but spent at shops,
 * so SHUWA owes those shops what they accepted. Batch what a business is
 * owed into a payout, send the money, then record it here as paid.
 */
export default function GiftCardPayouts() {
  const [balances, setBalances] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(null);
  const [details, setDetails] = useState({});

  const load = () =>
    Promise.all([
      api.get("/admin/gift-card-payouts/balances").then(({ data }) => setBalances(data)),
      api.get("/admin/gift-card-payouts").then(({ data }) => setPayouts(data.data || [])),
    ]).catch(() => {
      setBalances((b) => b || []);
    });

  useEffect(() => {
    load();
  }, []);

  const run = async (id, fn) => {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const createPayout = (businessId) =>
    run(businessId, () => api.post("/admin/gift-card-payouts", { business_id: businessId }));

  const cancelPayout = (payout) => {
    if (!window.confirm(`Cancel the ${formatPrice(payout.amount)} payout to ${payout.business?.name}?`)) return;
    run(payout.id, () => api.post(`/admin/gift-card-payouts/${payout.id}/cancel`));
  };

  const toggleDetails = async (payout) => {
    if (details[payout.id]) {
      setDetails(({ [payout.id]: _, ...rest }) => rest);
      return;
    }
    const { data } = await api.get(`/admin/gift-card-payouts/${payout.id}`);
    setDetails((prev) => ({ ...prev, [payout.id]: data.redemptions }));
  };

  if (!balances) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div>
      <h1 className="font-montserrat text-2xl font-bold text-slate-900">Gift card payouts</h1>
      <p className="mb-6 mt-1 text-sm text-muted-foreground">
        Gift cards valid at any business are paid to SHUWA. When a shop accepts one, SHUWA owes it that amount.
      </p>

      {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

      <h2 className="mb-3 font-montserrat text-lg font-bold text-slate-900">Owed to businesses</h2>
      {balances.length === 0 ? (
        <p className="mb-8 text-sm text-muted-foreground">Nothing owed right now.</p>
      ) : (
        <div className="mb-8 space-y-3">
          {balances.map((b) => (
            <Card key={b.business?.id} className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">{b.business?.name}</p>
                  <p className="text-xs text-muted-foreground break-words">
                    {b.redemptions_count} redemption{b.redemptions_count === 1 ? "" : "s"} since{" "}
                    {new Date(b.oldest_at).toLocaleDateString()}
                    {b.business?.phone && ` · ${b.business.phone}`}
                    {b.business?.email && ` · ${b.business.email}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-slate-900">{formatPrice(b.amount)}</span>
                  <Button size="sm" onClick={() => createPayout(b.business?.id)} disabled={busyId === b.business?.id}>
                    Create payout
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <h2 className="mb-3 font-montserrat text-lg font-bold text-slate-900">Payouts</h2>
      {payouts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No payouts yet.</p>
      ) : (
        <div className="space-y-3">
          {payouts.map((p) => (
            <Card key={p.id} className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">
                    {p.business?.name} · {formatPrice(p.amount)}
                  </p>
                  <p className="text-xs text-muted-foreground break-words">
                    {p.redemptions_count} redemption{p.redemptions_count === 1 ? "" : "s"} · created{" "}
                    {new Date(p.created_at).toLocaleDateString()}
                    {p.status === "paid" &&
                      ` · paid ${new Date(p.paid_at).toLocaleDateString()} by ${METHOD_LABEL[p.method] || p.method}` +
                        (p.reference ? ` (ref ${p.reference})` : "") +
                        (p.payer?.full_name ? ` · ${p.payer.full_name}` : "")}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={STATUS_VARIANT[p.status]}>{p.status}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => toggleDetails(p)}>
                    {details[p.id] ? "Hide items" : "Items"}
                  </Button>
                  {p.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => setPaying(p)} disabled={busyId === p.id}>
                        Mark paid
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => cancelPayout(p)} disabled={busyId === p.id}>
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {details[p.id] && (
                <div className="mt-3 divide-y divide-border rounded-xl border border-border text-sm">
                  {details[p.id].map((r) => (
                    <div key={r.id} className="flex justify-between gap-3 p-2">
                      <span className="font-mono text-slate-900">{r.gift_card?.code}</span>
                      <span className="text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
                      <span className="font-semibold text-slate-900">{formatPrice(r.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {paying && (
        <MarkPaidDialog
          payout={paying}
          onClose={() => setPaying(null)}
          onSubmit={(values) =>
            run(paying.id, async () => {
              await api.post(`/admin/gift-card-payouts/${paying.id}/paid`, values);
              setPaying(null);
            })
          }
          busy={busyId === paying.id}
        />
      )}
    </div>
  );
}

function MarkPaidDialog({ payout, onClose, onSubmit, busy }) {
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const needsReference = method !== "cash";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <form
        role="dialog"
        aria-modal="true"
        aria-label="Mark payout paid"
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ method, reference: reference || null, notes: notes || null });
        }}
      >
        <h2 className="font-montserrat text-lg font-bold text-slate-900">Mark payout paid</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          {formatPrice(payout.amount)} to {payout.business?.name}. Record this only once the money has been sent.
        </p>

        <label htmlFor="payout-method" className="mb-1 block text-xs text-muted-foreground">
          Sent by
        </label>
        <select
          id="payout-method"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          className="mb-3 h-11 w-full rounded-xl border border-input bg-white px-3 text-sm"
        >
          {METHODS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>

        <label htmlFor="payout-reference" className="mb-1 block text-xs text-muted-foreground">
          Transfer reference{needsReference ? "" : " (optional)"}
        </label>
        <Input id="payout-reference" value={reference} onChange={(e) => setReference(e.target.value)} className="mb-3" />

        <label htmlFor="payout-notes" className="mb-1 block text-xs text-muted-foreground">
          Notes (optional)
        </label>
        <textarea
          id="payout-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="mb-4 w-full rounded-xl border border-input p-3 text-sm"
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy || (needsReference && !reference.trim())}>
            {busy ? "Saving…" : "Mark paid"}
          </Button>
        </div>
      </form>
    </div>
  );
}
